<template>
  <div class="input-tag-marcadores-field-renderer">
    <div class="input-tag-marcadores-field-renderer__label">
      {{ campo.label }}
    </div>
    <div class="input-tag-marcadores-field-renderer__input">
      <F360Marcadores
        :marcadores="marcadores"
        @marcador-click="handleMarcadorClick"
        style="margin-bottom: 0.5rem"
      />
      <PInputTextarea
        ref="textareaRef"
        :model-value="texto"
        :placeholder="campo.placeholder ?? campo.descricao ?? ''"
        :rows="2"
        style="width: 100%"
        :required="campo.obrigatorio"
        :error="Boolean(error)"
        @update:model-value="handleInput"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { TIntegracaoCampoModel } from "../../../@types/IntegracaoCampoModel";
import F360Marcadores from "@/ui/components/Inputs/Marcadores/F360Marcadores.vue";
import { PInputTextarea } from "@f360-dev/f360-prisma";

const props = defineProps<{
  campo: TIntegracaoCampoModel;
  modelValue?: unknown;
  error?: string;
}>();

const textareaRef = ref<InstanceType<typeof PInputTextarea>>();

const getNativeTextarea = (): HTMLTextAreaElement | null => {
  const root = (textareaRef.value as unknown as { $el?: Node })?.$el;
  if (!root) return null;

  if (root instanceof HTMLTextAreaElement) return root;

  const scope =
    root instanceof Element
      ? root
      : (root.parentElement ?? (root as ChildNode).parentNode);

  if (scope instanceof Element || scope instanceof Document) {
    return scope.querySelector("textarea");
  }

  return null;
};

const juntarValoresComoTextoDoCampo = (valores: readonly unknown[]): string => {
  return valores.map(String).join(", ");
};

const valorFormularioParaTexto = (valor: unknown): string => {
  if (valor === null || valor === undefined) return "";
  if (Array.isArray(valor)) return juntarValoresComoTextoDoCampo(valor);

  return String(valor);
};

const textoInicialDoValorPadrao = (): string => {
  const valorPadrao = props.campo.valorPadrao;

  if (Array.isArray(valorPadrao) && valorPadrao.length > 0) {
    return juntarValoresComoTextoDoCampo(valorPadrao);
  }
  if (typeof valorPadrao === "string") return valorPadrao;

  return "";
};

const texto = ref(
  valorFormularioParaTexto(props.modelValue) || textoInicialDoValorPadrao()
);

watch(
  () => props.modelValue,
  (valor) => {
    const textoDerivado = valorFormularioParaTexto(valor);

    if (textoDerivado !== texto.value) texto.value = textoDerivado;
  }
);

const marcadores = computed(() => {
  return props.campo.opcoes?.map((opcao) => opcao.label) ?? [];
});

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
}>();

const handleInput = (value: string) => {
  texto.value = value;
  emit("update:modelValue", value);
};

const handleMarcadorClick = async (marcador: string) => {
  const textarea = getNativeTextarea();
  const atual = texto.value;

  const inicio = textarea?.selectionStart ?? atual.length;
  const fim = textarea?.selectionEnd ?? atual.length;

  const antes = atual.slice(0, inicio);
  const depois = atual.slice(fim);

  const sepAntes = antes && !/\s$/.test(antes) ? " " : "";
  const sepDepois = depois && !/^\s/.test(depois) ? " " : "";

  const insercao = `${sepAntes}${marcador}${sepDepois}`;
  const novoTexto = `${antes}${insercao}${depois}`;

  texto.value = novoTexto;
  emit("update:modelValue", novoTexto);

  const proximoCursor = inicio + insercao.length;
  await nextTick();
  textarea?.focus();
  textarea?.setSelectionRange(proximoCursor, proximoCursor);
};
</script>

<style scoped lang="scss">
.input-tag-marcadores-field-renderer {
  width: 100%;

  &__label {
    font-family: $fontFamily;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    margin: 0 0 0.5rem 0;
    color: #2e2e2e;
    text-wrap: nowrap;
  }

  &__input {
    width: 100%;

    :deep(.el-form-item),
    :deep(.el-textarea) {
      margin-bottom: 0;
    }

    :deep(.el-textarea__inner) {
      font-size: 12px;
    }
  }

  :deep(.f360-marcadores__item) {
    font-size: 12px;
  }
}
</style>
